
import { defineComponent, ref, onMounted } from "vue";
import { MBDatatable } from "magicbean-saas-common";
import addGroupModal from "@/views/merchant-network/groups/AddGroupModal.vue";
import FilterDropdwon from "@/views/merchant-network/groups/FilterDropdown.vue";
import { MenuComponent } from "@/assets/ts/components";
import { ApiGroup } from "@/core/api";
import moment from "moment";
import _ from "lodash";
import fileDownload from "js-file-download";
import { formatDateTime, setModuleBCN } from "@/core/directive/function/common";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

interface Group {
  id: number;
  name: string;
  short_key: string;
  city: string;
  country_iso_2: string;
  telephone: string;
}
interface SortOption {
  field: string;
  direction: string;
}

interface FilterOption {
  field: string;
  value: string;
  from?: string;
  to?: number;
  condition?: string;
}

export default defineComponent({
  name: "Group",
  components: {
    MBDatatable,
    addGroupModal,
    FilterDropdwon,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const loading = ref(true);
    const disabledExport = ref(false);
    const tableData = ref<Array<Group>>([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(10);
    const search = ref<string>("");
    const sortOptions = ref<Array<SortOption>>([
      {
        field: "id",
        direction: "desc",
      },
    ]);
    const filterOptions = ref<Array<FilterOption>>([
      {
        field: "status",
        value: "10",
      },
    ]);

    const tableHeader = ref([
      {
        name: "Group ID",
        key: "id",
        sortable: true,
      },
      {
        name: "Group Name",
        key: "name",
        sortable: true,
      },
      {
        name: "Short Key",
        key: "short_key",
        sortable: true,
      },
      {
        name: "Country",
        key: "country_iso_2",
        sortable: true,
      },
      // {
      //   name: "City",
      //   key: "city",
      //   sortable: true,
      // },
      // {
      //   name: "Telephone",
      //   key: "telephone",
      //   sortable: true,
      // },
      {
        name: "Status",
        key: "status",
        sortable: true,
      },
      {
        name: "Creation time",
        key: "created_at",
        sortable: true,
      },
      {
        name: t("common.action"),
        key: "actions",
      },
    ]);

    onMounted(() => {
      MenuComponent.reinitialization();
      setModuleBCN(t, route);
      getGroupList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    });

    const getGroupList = (
      page: number,
      pageSize: number,
      search?: string,
      sortOptions?: Array<SortOption>,
      filterOptions?: Array<FilterOption>
    ) => {
      loading.value = true;
      ApiGroup.getGroupList({
        page: page,
        page_size: pageSize,
        search_value: search,
        sort_orders: sortOptions,
        filter_group: filterOptions,
      })
        .then(({ data }) => {
          loading.value = false;
          if (data.code == 0) {
            tableData.value.splice(
              0,
              tableData.value.length,
              ...data.data.items
            );
            total.value = data.data.total;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const onCurrentPageChange = (page) => {
      currentPage.value = page;
      getGroupList(
        page,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const onRowsPerPageChange = (pagesize) => {
      pageSize.value = pagesize;
      getGroupList(
        currentPage.value,
        pagesize,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const handleFilter = (val) => {
      console.log(val);
      let filterArr: Array<FilterOption> = [];
      for (let item in val) {
        if (val[item] != "") {
          filterArr.push({
            field: item,
            value: val[item],
            condition: "like",
          });
        }
      }
      filterOptions.value.splice(0, filterOptions.value.length, ...filterArr);
      getGroupList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const handleFilterReset = () => {
      resetFilter();
      getGroupList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    const onColumnSort = (val) => {
      let sortObj: SortOption = {
        field: val.columnName,
        direction: val.order,
      };
      sortOptions.value.splice(0, sortOptions.value.length, sortObj);
      getGroupList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const debounceSearch = _.debounce(getGroupList, 1000);

    const searchItems = () => {
      debounceSearch(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const updateLists = () => {
      getGroupList(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const fileExport = () => {
      disabledExport.value = true;
      ApiGroup.groupPrint({
        search_value: search.value,
        filter_group: filterOptions.value,
        sort_orders: sortOptions.value,
        download_data_type: "stream",
      })
        .then((data) => {
          disabledExport.value = false;
          const disposition = data.headers["content-disposition"];
          fileDownload(data.data, disposition.split("filename=")[1]);
        })
        .catch((error) => {
          disabledExport.value = false;
          console.log(error);
        });
    };

    return {
      formatDateTime,
      loading,
      tableHeader,
      disabledExport,
      tableData,
      currentPage,
      total,
      pageSize,
      search,
      getGroupList,
      onCurrentPageChange,
      onRowsPerPageChange,
      searchItems,
      onColumnSort,
      handleFilter,
      handleFilterReset,
      updateLists,
      fileExport,
    };
  },
});
